import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { toast } from 'sonner';
import {
  YBtn, YIconBtn, YField, YInput, YSelect, YBadge, YAvatar, YTabs,
  colorFor, initialsOf,
} from '../../components/yool/YoolPrimitives';
import { YIcon } from '../../components/yool/YoolIcons';
import {
  getEmpresas,
  getPlanesHabilitados,
  getPlanIdsUsadosEnEmpresas,
  addPlanHabilitado,
  removePlanHabilitado,
  getPlanesCentral,
  createEmpresa,
  updateEmpresa,
  toggleEmpresaEstado,
  crearOrgClerk,
} from '../../services/empresasPlanesService';
import { getPolizasByIds } from '../../services/divinaPolizasService';

const EMPTY_EMPRESA = {
  razon_social: '', nombre_fantasia: '', rut: '', slug: '', giro: '',
  correo_contacto: '', telefono: '', direccion: '', numero: '', oficina: '',
  comuna: '', region: '', color_principal: '#007D6E', logo_url: '',
  imagen_url: '', pago_empresa: false, estado: 'activa',
};

function EmpresaForm({ empresa, onSave, saving, dirty, setDirty }) {
  const [form, setForm] = useState(empresa || EMPTY_EMPRESA);

  useEffect(() => {
    setForm(empresa || EMPTY_EMPRESA);
    setDirty(false);
  }, [empresa?.id]);

  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setDirty(true); };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.razon_social.trim()) { toast.error('La razón social es requerida'); return; }
    onSave(form);
  };

  return (
    <form onSubmit={handleSubmit} className="y-form-body" style={{ paddingBottom: 96 }}>
      <div className="y-form-section">
        <div className="y-form-section-head">
          <div className="y-form-section-eyebrow">Identidad</div>
          <div className="y-form-section-desc">Datos legales y comerciales.</div>
        </div>
        <div className="y-grid-2">
          <YField label="Razón social" required>
            <YInput value={form.razon_social} onChange={e => set('razon_social', e.target.value)} placeholder="Demo Company Spa" />
          </YField>
          <YField label="Nombre fantasía">
            <YInput value={form.nombre_fantasia} onChange={e => set('nombre_fantasia', e.target.value)} />
          </YField>
          <YField label="RUT">
            <YInput value={form.rut} onChange={e => set('rut', e.target.value)} placeholder="77.777.777-7" />
          </YField>
          <YField label="Slug" hint={form.slug ? `URL pública: /empresa/${form.slug}` : undefined}>
            <YInput value={form.slug} onChange={e => set('slug', e.target.value)} placeholder="nombre-empresa" />
          </YField>
          <YField label="Giro" span={2}>
            <YInput value={form.giro || ''} onChange={e => set('giro', e.target.value)} placeholder="¿A qué se dedica?" />
          </YField>
        </div>
      </div>

      <div className="y-form-section">
        <div className="y-form-section-head">
          <div className="y-form-section-eyebrow">Contacto</div>
        </div>
        <div className="y-grid-2">
          <YField label="Correo de contacto">
            <YInput type="email" value={form.correo_contacto || ''} onChange={e => set('correo_contacto', e.target.value)} placeholder="tu@empresa.cl" />
          </YField>
          <YField label="Teléfono">
            <YInput value={form.telefono || ''} onChange={e => set('telefono', e.target.value)} placeholder="+56 9 0000 0000" />
          </YField>
        </div>
      </div>

      <div className="y-form-section">
        <div className="y-form-section-head">
          <div className="y-form-section-eyebrow">Identidad visual</div>
          <div className="y-form-section-desc">Se usa en la página de bienvenida de tus colaboradores.</div>
        </div>
        <div className="y-grid-2">
          <YField label="Color principal" hint="Hex en formato #RRGGBB.">
            <div className="y-field-color-wrap">
              <span className="y-color-swatch" style={{ background: form.color_principal || '#fff' }}>
                <input type="color" value={form.color_principal || '#000000'} onChange={e => set('color_principal', e.target.value)} />
              </span>
              <YInput value={form.color_principal || ''} onChange={e => set('color_principal', e.target.value)} placeholder="#RRGGBB" />
            </div>
          </YField>
          <YField label="Logo (URL)" hint="PNG o SVG con fondo transparente.">
            <div className="y-field-color-wrap">
              <span className="y-logo-thumb">
                {form.logo_url
                  ? <img src={form.logo_url} alt="" onError={e => { e.target.style.display = 'none'; }} />
                  : <YIcon.Building size={18} style={{ color: 'var(--yool-muted)' }} />}
              </span>
              <YInput value={form.logo_url || ''} onChange={e => set('logo_url', e.target.value)} placeholder="https://…" />
            </div>
          </YField>
          {/* La consume flow en /empresa/[slug]: EmpresaHero hace
              heroImageUrl = empresaImageUrl ?? HERO_IMAGE, así que sin esto la
              portada queda con la foto genérica de picsum. Se le aplica encima un
              tinte con el color principal. */}
          <YField
            label="Imagen de portada (URL)"
            span={2}
            hint="Foto del encabezado en /empresa/[slug]. Horizontal, mínimo 800×1100. Sin imagen se usa una genérica."
          >
            <div className="y-field-color-wrap">
              <span className="y-logo-thumb">
                {form.imagen_url
                  ? <img src={form.imagen_url} alt="" onError={e => { e.target.style.display = 'none'; }} />
                  : <YIcon.FileText size={18} style={{ color: 'var(--yool-muted)' }} />}
              </span>
              <YInput value={form.imagen_url || ''} onChange={e => set('imagen_url', e.target.value)} placeholder="https://…" />
            </div>
          </YField>
        </div>
      </div>

      <div className="y-form-section">
        <div className="y-form-section-head">
          <div className="y-form-section-eyebrow">Domicilio</div>
        </div>
        <div className="y-grid-4">
          <YField label="Dirección" span={2}>
            <YInput value={form.direccion || ''} onChange={e => set('direccion', e.target.value)} placeholder="Calle / avenida" />
          </YField>
          <YField label="Número">
            <YInput value={form.numero || ''} onChange={e => set('numero', e.target.value)} />
          </YField>
          <YField label="Oficina / Dpto">
            <YInput value={form.oficina || ''} onChange={e => set('oficina', e.target.value)} />
          </YField>
          <YField label="Comuna">
            <YInput value={form.comuna || ''} onChange={e => set('comuna', e.target.value)} />
          </YField>
          <YField label="Región">
            <YInput value={form.region || ''} onChange={e => set('region', e.target.value)} />
          </YField>
        </div>
      </div>

      <div className="y-form-section">
        <div className="y-form-section-head">
          <div className="y-form-section-eyebrow">Estado y pago</div>
        </div>
        <div className="y-grid-2">
          <YField label="Pago empresa" hint="¿La empresa absorbe el costo del plan?">
            <YSelect value={form.pago_empresa ? 'si' : 'no'} onChange={e => set('pago_empresa', e.target.value === 'si')}>
              <option value="no">No — paga cada colaborador</option>
              <option value="si">Sí — paga la empresa</option>
            </YSelect>
          </YField>
          <YField label="Estado">
            {/* El CHECK de empresa_empleadores.estado solo acepta
                pendiente_onboarding | activa | suspendida | inactiva. En masculino
                el insert se cae con un 400 (23514). */}
            <YSelect value={form.estado || 'activa'} onChange={e => set('estado', e.target.value)}>
              <option value="activa">Activa</option>
              <option value="inactiva">Inactiva</option>
            </YSelect>
          </YField>
        </div>
      </div>
    </form>
  );
}

/**
 * Acceso de la empresa al portal: crea su organización en Clerk y la vincula.
 *
 * Vive aquí y no dentro de EmpresaForm porque `clerk_org_id` NO es un campo
 * editable — el endpoint lo deja fuera de la whitelist a propósito, ya que quien
 * pueda escribirlo se apropia de la empresa vía las políticas RLS por org. Es una
 * acción explícita, y una sola vez: si ya está vinculada, solo se muestra.
 */
function AccesoPortal({ empresa, getToken, onVinculada }) {
  const [email, setEmail] = useState('');
  const [creando, setCreando] = useState(false);

  const handleCrear = async () => {
    try {
      setCreando(true);
      const r = await crearOrgClerk(getToken, empresa.id, email.trim() || null);
      toast.success(
        r.invitacion
          ? `Organización creada y ${r.invitacion.email} invitado como administrador`
          : 'Organización creada y vinculada'
      );
      // El vínculo quedó hecho aunque la invitación falle: se avisa aparte para
      // que no parezca que hay que repetir todo.
      if (r.error_invitacion) toast.error(`La invitación falló: ${r.error_invitacion}`);
      onVinculada(r.empresa);
    } catch (e) {
      toast.error(e.message || 'No se pudo crear la organización');
    } finally {
      setCreando(false);
    }
  };

  return (
    <div className="y-form-section">
      <div className="y-form-section-head">
        <div className="y-form-section-eyebrow">Acceso al portal</div>
        <div className="y-form-section-desc">
          {empresa.clerk_org_id
            ? 'La empresa puede entrar al portal con su propia cuenta.'
            : 'Sin organización, la empresa no puede entrar al portal: todas sus pantallas filtran por organización.'}
        </div>
      </div>

      {empresa.clerk_org_id ? (
        <button
          className="y-url-pill"
          title="Copiar el ID de organización"
          onClick={() => {
            navigator.clipboard?.writeText(empresa.clerk_org_id);
            toast.success('ID de organización copiado');
          }}
        >
          <YIcon.ExternalLink size={12} />
          {empresa.clerk_org_id}
        </button>
      ) : (
        <div className="y-grid-2">
          <YField label="Email del administrador" hint="Recibe la invitación para crear su cuenta. Opcional: se puede invitar después.">
            <YInput
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="nombre@empresa.cl"
            />
          </YField>
          <YField label="&nbsp;">
            <YBtn variant="outline" onClick={handleCrear} disabled={creando}>
              {creando ? 'Creando...' : 'Crear organización y dar acceso'}
            </YBtn>
          </YField>
        </div>
      )}
    </div>
  );
}

function PlanesTab({ empresa, planesCentral }) {
  const [planesHabilitados, setPlanesHabilitados] = useState([]);
  const [planIdsEnUso, setPlanIdsEnUso] = useState(new Set());
  const [polizasPorId, setPolizasPorId] = useState({});
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (empresa?.id) loadData();
  }, [empresa?.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [habilitados, enUso] = await Promise.all([
        getPlanesHabilitados(empresa.id),
        getPlanIdsUsadosEnEmpresas(),
      ]);
      setPlanesHabilitados(habilitados);
      setPlanIdsEnUso(enUso);

      // Vinculación con póliza colectiva (empresa_planes_habilitados.poliza_colectiva_id) —
      // sin esto, este panel no dejaba ver qué plan habilitado ya está respaldado
      // por una póliza real y cuál todavía no.
      const polizaIds = [...new Set(habilitados.map((h) => h.poliza_colectiva_id).filter(Boolean))];
      if (polizaIds.length > 0) {
        const polizas = await getPolizasByIds(polizaIds);
        setPolizasPorId(Object.fromEntries(polizas.map((p) => [p.id, p])));
      } else {
        setPolizasPorId({});
      }
    } catch {
      toast.error('Error al cargar planes habilitados');
    } finally {
      setLoading(false);
    }
  };

  const handleAgregar = async () => {
    if (!selectedPlanId) return;
    try {
      setAdding(true);
      await addPlanHabilitado(empresa.id, selectedPlanId);
      toast.success('Plan habilitado');
      setSelectedPlanId('');
      await loadData();
    } catch (e) {
      toast.error(e.message || 'Error al habilitar plan');
    } finally {
      setAdding(false);
    }
  };

  const handleRemover = async (id) => {
    try {
      await removePlanHabilitado(id);
      toast.success('Plan deshabilitado');
      await loadData();
    } catch {
      toast.error('Error al deshabilitar plan');
    }
  };

  const planesYaHab = new Set(planesHabilitados.map(ph => ph.plan_id));
  const planesDisponibles = planesCentral.filter(p => !planesYaHab.has(p.id));
  const getNombre = (planId) => planesCentral.find(p => p.id === planId)?.nombre || planId;

  const plansPorProducto = planesDisponibles.reduce((acc, p) => {
    const key = p.producto?.nombre || 'Sin producto';
    if (!acc[key]) acc[key] = [];
    acc[key].push(p);
    return acc;
  }, {});

  return (
    <div className="y-form-body">
      <div className="y-add-bar">
        <YField label="Habilitar plan central para esta empresa">
          <YSelect value={selectedPlanId} onChange={e => setSelectedPlanId(e.target.value)}>
            <option value="">Selecciona un plan…</option>
            {Object.entries(plansPorProducto).map(([producto, planes]) => (
              <optgroup key={producto} label={producto}>
                {planes.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.nombre}{p.solo_empresa ? '  · exclusivo' : ''}
                  </option>
                ))}
              </optgroup>
            ))}
          </YSelect>
        </YField>
        <YBtn
          variant="primary"
          size="sm"
          icon={<YIcon.Plus size={13} />}
          onClick={handleAgregar}
          disabled={!selectedPlanId || adding}
        >
          Habilitar
        </YBtn>
        <YIconBtn variant="ghost" size="sm" title="Recargar" onClick={loadData}>
          <YIcon.Refresh size={13} />
        </YIconBtn>
      </div>

      {loading ? (
        <p style={{ fontSize: 13, color: 'var(--yool-muted)' }}>Cargando planes...</p>
      ) : planesHabilitados.length === 0 ? (
        <div className="y-list-empty">Esta empresa todavía no tiene planes habilitados.</div>
      ) : (
        <div>
          {planesHabilitados.map(ph => {
            const poliza = ph.poliza_colectiva_id ? polizasPorId[ph.poliza_colectiva_id] : null;
            return (
              <div key={ph.id} className="y-row">
                <div>
                  <div className="y-row-head">
                    <span className="y-row-title">{ph.nombre_interno || getNombre(ph.plan_id)}</span>
                    <YBadge tone="outline">{ph.ambiente}</YBadge>
                    {planesCentral.find(p => p.id === ph.plan_id)?.solo_empresa && (
                      <YBadge tone="cyan">Exclusivo</YBadge>
                    )}
                    {ph.poliza_colectiva_id ? (
                      <Link to={`/product_config/polizas/edit/${ph.poliza_colectiva_id}`}>
                        <YBadge tone="success" dot>
                          Vinculada{poliza?.numero_poliza ? ` · ${poliza.numero_poliza}` : ''}
                          {poliza?.compania?.nombre_corto ? ` (${poliza.compania.nombre_corto})` : ''}
                        </YBadge>
                      </Link>
                    ) : (
                      <YBadge tone="muted">Sin vincular a póliza</YBadge>
                    )}
                  </div>
                </div>
                <div className="y-row-right">
                  {ph.precio_uf_override && (
                    <div className="y-row-amount">
                      <span className="y-unit">UF</span>{ph.precio_uf_override}
                    </div>
                  )}
                  <div className="y-row-actions">
                    <YIconBtn variant="danger" size="sm" title="Deshabilitar" onClick={() => handleRemover(ph.id)}>
                      <YIcon.Trash size={13} />
                    </YIconBtn>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function EmpresasPlanesManager() {
  const [empresas, setEmpresas] = useState([]);
  const [planesCentral, setPlanesCentral] = useState([]);
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('info');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // empresa_empleadores va por /api/empresas con el JWT de Clerk (ver el service).
  const { getToken } = useAuth();

  useEffect(() => {
    loadEmpresas();
    loadPlanesCentral();
  }, []);

  const loadEmpresas = async () => {
    try {
      setLoading(true);
      const data = await getEmpresas(getToken);
      setEmpresas(data);
    } catch (e) {
      // Se muestra el mensaje real: antes un 401 o un error del endpoint quedaba
      // indistinguible de "no hay empresas".
      toast.error(e.message || 'Error al cargar empresas');
    } finally {
      setLoading(false);
    }
  };

  const loadPlanesCentral = async () => {
    try {
      const data = await getPlanesCentral();
      setPlanesCentral(data);
    } catch {
      toast.error('Error al cargar planes');
    }
  };

  const handleSave = async (form) => {
    try {
      setSaving(true);
      if (isCreating) {
        const nueva = await createEmpresa(getToken, form);
        toast.success('Empresa creada');
        await loadEmpresas();
        setSelectedEmpresa(nueva);
        setIsCreating(false);
      } else {
        const updated = await updateEmpresa(getToken, selectedEmpresa.id, form);
        toast.success('Empresa actualizada');
        setSelectedEmpresa(updated);
        await loadEmpresas();
      }
      setDirty(false);
    } catch (e) {
      toast.error(e.message || 'Error al guardar empresa');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEstado = async () => {
    const nuevoEstado = selectedEmpresa.estado === 'activa' ? 'inactiva' : 'activa';
    try {
      const updated = await toggleEmpresaEstado(getToken, selectedEmpresa.id, nuevoEstado);
      toast.success(`Empresa ${nuevoEstado}`);
      setSelectedEmpresa(updated);
      await loadEmpresas();
    } catch (e) {
      toast.error(e.message || 'Error al cambiar estado');
    }
  };

  const empresasFiltradas = empresas.filter(e => {
    const q = search.toLowerCase();
    return (
      e.razon_social?.toLowerCase().includes(q) ||
      e.nombre_fantasia?.toLowerCase().includes(q) ||
      e.rut?.toLowerCase().includes(q) ||
      e.slug?.toLowerCase().includes(q)
    );
  });

  const isActivo = selectedEmpresa?.estado !== 'inactiva';

  return (
    <div className="y-workspace">
      {/* Panel izquierdo */}
      <div className="y-master">
        <div className="y-master-head">
          <div className="y-master-title">
            <YIcon.Building size={14} style={{ color: 'var(--yool-muted)', flexShrink: 0 }} />
            <span className="y-fy">
              Empresas <span className="fy-teal">empleadoras</span>
            </span>
            <span className="y-count">{empresasFiltradas.length}</span>
          </div>
          <div className="y-master-search">
            <YIcon.Search />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar por nombre, RUT o slug…"
            />
          </div>
          <div className="y-master-actions">
            <YBtn
              variant="outline"
              size="sm"
              icon={<YIcon.Plus size={13} />}
              style={{ flex: 1 }}
              onClick={() => { setIsCreating(true); setSelectedEmpresa(null); setTab('info'); }}
            >
              Nueva empresa
            </YBtn>
            <YIconBtn variant="ghost" size="sm" title="Recargar" onClick={loadEmpresas}>
              <YIcon.Refresh size={13} />
            </YIconBtn>
          </div>
        </div>

        <div className="y-master-list">
          {loading ? (
            <div className="y-master-empty">Cargando...</div>
          ) : empresasFiltradas.length === 0 ? (
            <div className="y-master-empty">Ajustemos los filtros y la encontramos.</div>
          ) : empresasFiltradas.map(emp => {
            const display = emp.nombre_fantasia || emp.razon_social;
            const inactive = emp.estado === 'inactiva';
            const active = !isCreating && selectedEmpresa?.id === emp.id;
            return (
              <div
                key={emp.id}
                className={`y-list-item${active ? ' active' : ''}`}
                onClick={() => { setSelectedEmpresa(emp); setIsCreating(false); setTab('info'); }}
              >
                <YAvatar
                  initials={initialsOf(display)}
                  color={emp.color_principal || colorFor(emp.id)}
                  logoUrl={emp.logo_url || null}
                  size={32}
                />
                <div className="y-list-item-body">
                  <div className="y-list-item-name">
                    <span className={`y-status-dot${inactive ? ' inactive' : ''}`} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{display}</span>
                  </div>
                  <div className="y-list-item-meta">{emp.rut || '—'}</div>
                  <div className="y-list-item-tags">
                    {emp.slug && <YBadge tone="outline">{emp.slug}</YBadge>}
                    {emp.pago_empresa && <YBadge tone="soft">pago empresa</YBadge>}
                    {inactive && <YBadge tone="muted">inactiva</YBadge>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Panel derecho */}
      <div className="y-detail">
        {isCreating ? (
          <div className="y-detail-card">
            <div className="y-detail-head">
              <div style={{
                width: 48, height: 48, borderRadius: 14,
                background: 'var(--yool-navy)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', flexShrink: 0,
              }}>
                <YIcon.Building size={22} />
              </div>
              <div>
                <div className="y-detail-head-eyebrow">Empresa empleadora</div>
                <div className="y-detail-head-title">Nueva empresa</div>
                <div className="y-detail-head-meta">Completa los datos para habilitar planes.</div>
              </div>
            </div>
            <EmpresaForm onSave={handleSave} saving={saving} dirty={dirty} setDirty={setDirty} />
            <div className="y-form-footer">
              <div className="y-form-footer-meta">
                Campos con <span style={{ color: 'var(--yool-cta)', fontWeight: 700 }}>*</span> son obligatorios.
              </div>
              <div className="y-form-footer-actions">
                <YBtn variant="quiet" onClick={() => setIsCreating(false)}>Cancelar</YBtn>
                <YBtn
                  variant="primary"
                  disabled={saving}
                  onClick={() => document.querySelector('.y-detail-card form')?.requestSubmit()}
                >
                  {saving ? 'Guardando...' : 'Crear empresa'}
                </YBtn>
              </div>
            </div>
          </div>
        ) : !selectedEmpresa ? (
          <div className="y-detail-empty">
            <div className="y-glyph"><YIcon.Building size={28} /></div>
            <div>
              <h3>Selecciona una empresa</h3>
              <p>Elige una de la lista o crea <span className="fy-teal">una nueva</span> para configurar sus planes habilitados.</p>
            </div>
            <YBtn variant="outline" icon={<YIcon.Plus size={13} />} onClick={() => setIsCreating(true)}>
              Nueva empresa
            </YBtn>
          </div>
        ) : (
          <div className="y-detail-card">
            <div className="y-detail-head">
              <YAvatar
                size={48}
                initials={initialsOf(selectedEmpresa.nombre_fantasia || selectedEmpresa.razon_social)}
                color={selectedEmpresa.color_principal || colorFor(selectedEmpresa.id)}
                logoUrl={selectedEmpresa.logo_url || null}
              />
              <div style={{ minWidth: 0 }}>
                <div className="y-detail-head-eyebrow">
                  Empresa empleadora · {selectedEmpresa.rut || '—'}
                </div>
                <div className="y-detail-head-title">
                  {selectedEmpresa.nombre_fantasia || selectedEmpresa.razon_social}
                  <YBadge tone={isActivo ? 'success' : 'muted'} dot>
                    {isActivo ? 'activa' : 'inactiva'}
                  </YBadge>
                </div>
                <div className="y-detail-head-meta">
                  {selectedEmpresa.comuna && (
                    <span>{selectedEmpresa.comuna}, {selectedEmpresa.region}</span>
                  )}
                  {selectedEmpresa.comuna && <span className="y-dot">·</span>}
                  <span>{selectedEmpresa.giro || 'Sin giro registrado'}</span>
                  {selectedEmpresa.color_principal && (
                    <>
                      <span className="y-dot">·</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span className="y-color-chip" style={{ background: selectedEmpresa.color_principal }} />
                        {selectedEmpresa.color_principal.toUpperCase()}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="y-detail-head-actions">
                {selectedEmpresa.slug && (
                  <button className="y-url-pill" title="Copiar URL pública" onClick={() => {
                    navigator.clipboard?.writeText(`/empresa/${selectedEmpresa.slug}`);
                    toast.success('URL copiada');
                  }}>
                    <YIcon.ExternalLink size={12} />
                    /empresa/{selectedEmpresa.slug}
                  </button>
                )}
                <YIconBtn
                  variant="quiet"
                  title={isActivo ? 'Desactivar empresa' : 'Activar empresa'}
                  onClick={handleToggleEstado}
                >
                  <YIcon.Power size={16} />
                </YIconBtn>
                <YIconBtn variant="quiet" title="Mas opciones">
                  <YIcon.Dots size={16} />
                </YIconBtn>
              </div>
            </div>

            <YTabs
              value={tab}
              onChange={setTab}
              tabs={[
                { id: 'info', label: 'Información' },
                { id: 'planes', label: 'Planes habilitados' },
              ]}
            />

            {tab === 'info' && (
              <>
                <EmpresaForm
                  empresa={selectedEmpresa}
                  onSave={handleSave}
                  saving={saving}
                  dirty={dirty}
                  setDirty={setDirty}
                />
                <AccesoPortal
                  empresa={selectedEmpresa}
                  getToken={getToken}
                  onVinculada={(empresa) => { setSelectedEmpresa(empresa); loadEmpresas(); }}
                />
                <div className="y-form-footer">
                  <div className="y-form-footer-meta">
                    {dirty
                      ? <>Tienes <span style={{ color: 'var(--fy-orange)', fontWeight: 700 }}>cambios sin guardar</span>.</>
                      : <>Cambios sincronizados</>}
                  </div>
                  <div className="y-form-footer-actions">
                    <YBtn variant="quiet" disabled={!dirty} onClick={() => setDirty(false)}>Descartar</YBtn>
                    <YBtn
                      variant="primary"
                      disabled={!dirty || saving}
                      onClick={() => document.querySelector('.y-detail-card form')?.requestSubmit()}
                    >
                      {saving ? 'Guardando...' : 'Guardar cambios'}
                    </YBtn>
                  </div>
                </div>
              </>
            )}

            {tab === 'planes' && (
              <PlanesTab empresa={selectedEmpresa} planesCentral={planesCentral} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
